// Panel de edición de una prenda.
//
// Se abre dentro de la propia tarjeta, no en otra pantalla. Editar tres
// prendas seguidas no debería costar seis navegaciones.
//
// Manda al servidor SOLO lo que cambió. Importa por dos motivos:
//
//   1. `notas` guarda marcas internas de la migración. Mandarlas de
//      vuelta sin querer las duplicaría.
//   2. El nombre que el usuario trajo de Notion tiene detalles que la
//      IA no ve. Si no lo tocó, no se manda, y no hay forma de que se
//      pise por accidente.

import { useEffect, useState } from "react";
import * as api from "./api";
import NuevoColor from "./NuevoColor";
import NuevaOcasion from "./NuevaOcasion";
// La misma normalización que usa el servidor para decidir si dos
// nombres son el mismo: sin acentos y sin mayúsculas, PERO con los
// espacios. Hasta 2026-09-06 esto sacaba también los espacios y el
// comentario decía —falsamente— que era la del servidor: un usuario
// con "Azul Claro" y "AzulClaro" veía uno solo acá, y el otro quedaba
// inseleccionable. Ver BA-9 en src/tintes.js.
import { claveDeNombre } from "./tintes";
import { expandirEstaciones, atajoDeEstaciones } from "./opciones";
import { t, tCat, tError, tPlural, useIdioma, fecha as fechaLocal } from "./i18n";
import { IconoFichada } from "./Iconos";

// Comparación laxa: dos arrays con los mismos elementos en distinto
// orden no son un cambio. Sin esto, abrir el panel y cerrarlo mandaría
// `colores` cada vez.
function igual(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");
  }
  return (a ?? "") === (b ?? "");
}

/* ------------------------------------------------------------------
   La taxonomía del tipo elegido.

   Vive en `opciones` porque la sirve el servidor desde
   `lib/prendas_tipos.js`: dos copias de una lista de 103 nombres son
   dos cosas que algún día van a decir distinto.

   Un tipo sin entrada devuelve `{}` y el desplegable queda con la
   opción vacía sola. No debería pasar —los 6 tipos tienen prendas— pero
   romper la ficha entera por eso sería desproporcionado.
------------------------------------------------------------------ */
function taxonomiaDe(opciones, tipo) {
  return opciones?.taxonomia?.[tipo] || {};
}

// ¿La prenda elegida sigue perteneciendo al tipo nuevo?
//
// Casi siempre la respuesta es no, y entonces se limpia. Pero no
// siempre: si alguien vuelve al tipo anterior en el mismo rato, no hay
// razón para hacerle elegir de nuevo lo que ya había elegido.
function perteneceAlTipo(opciones, nombre, tipo) {
  if (!nombre) return false;
  return Object.values(taxonomiaDe(opciones, tipo)).some((lista) =>
    lista.includes(nombre)
  );
}

export default function EditarPrenda({
  prenda,
  opciones,
  onGuardar,
  onCancelar,
  onFichada,
}) {
  // Re-renderiza si cambia el idioma: `tCat` se resuelve en el render.
  useIdioma();
  const [fichando, setFichando] = useState(false);
  const [errorFichar, setErrorFichar] = useState("");
  const [f, setF] = useState({
    nombre: prenda.nombre || "",
    tipo: prenda.tipo,
    // Qué es exactamente. `""` es "todavía no lo sé", que es el estado
    // de las prendas cargadas antes de que existiera este dato.
    prenda: prenda.prenda || "",
    colores: prenda.colores || [],
    material: prenda.material || "",
    categorias: prenda.categorias || [],
    // Un CONJUNTO, no un valor. `expandirEstaciones` acepta el array
    // nuevo, el valor viejo ("Todo año") o nada, y siempre devuelve algo
    // válido — así una prenda sin migrar se edita igual.
    estaciones: expandirEstaciones(prenda.estaciones ?? prenda.estacion),
    estado: prenda.estado,
    disponibilidad: prenda.disponibilidad,
    marca: prenda.marca || "",
    talla: prenda.talla || "",
    notas: prenda.notas || "",
  });

  // Cuál de los atajos describe la selección actual, para marcarlo.
  // Se recalcula en cada render a propósito: es una comparación de
  // cuatro strings y guardarlo en estado sería una copia que se puede
  // desincronizar.
  const atajoActivo = atajoDeEstaciones(f.estaciones);

  // Tocar una estación la suma o la saca. Aditivo, igual que la
  // selección de prendas en "Armar a mano": el usuario dice qué SÍ, no
  // qué categoría encaja.
  function alternarEstacion(e2) {
    setF((a) => ({
      ...a,
      estaciones: a.estaciones.includes(e2)
        ? a.estaciones.filter((x) => x !== e2)
        : [...a.estaciones, e2],
    }));
  }

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  /* ══════════════════════════════════════════════════════════════
     LOS DOS GRUPOS DE CHIPS ARRANCAN PLEGADOS (2026-08-30)
     ══════════════════════════════════════════════════════════════

     Medido con el CSS real a 390px: el editor medía 1348px (1,9
     pantallas) y el bloque de colores solo —20 chips base más los
     propios— eran 261px, el 19% del formulario, para mostrar una
     paleta entera cuando la prenda tiene uno o dos colores.

     Plegado se ven LOS ELEGIDOS más «Cambiar»: 34px. La paleta
     completa se abre ahí mismo, entre los campos —el mismo patrón
     que CargaLote usa desde siempre con "Cambiar colores y
     ocasiones"— y no en un menú flotante: un panel que flota dentro
     del modal necesita posicionamiento, foco y espejado RTL para
     árabe y urdu, superficie nueva para el mismo espacio ganado.

     Los chips elegidos SIGUEN SIENDO TOCABLES estando plegados:
     sacar un color no obliga a abrir nada. «Cambiar» hace falta
     solo para AGREGAR. Así el plegado le cuesta un tap a la mitad
     de las ediciones, no a todas.

     Un huérfano (color u ocasión que ya no está en ninguna lista)
     se ve SIEMPRE, plegado o no: está elegido, y esconderlo haría
     que guardar lo pierda sin que nadie lo pida. */
  const [abierto, setAbierto] = useState({ colores: false, categorias: false });
  const abrir = (grupo) => setAbierto((a) => ({ ...a, [grupo]: true }));
  const cerrar = (grupo) => setAbierto((a) => ({ ...a, [grupo]: false }));

  /* Notas: 115px de textarea para un campo casi siempre vacío.
     Vacío arranca como un chip «+ Notas»; con contenido arranca
     abierto, porque esconder texto que existe sería mentir. */
  const [notasAbiertas, setNotasAbiertas] = useState(
    Boolean((prenda.notas || "").trim())
  );

  // Los colores propios se piden acá y no vienen en `opciones` porque
  // `/api/opciones` no tiene sesión: sirve las listas globales antes de
  // que el usuario entre. Los colores propios son de un usuario.
  const [propios, setPropios] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [muestras, setMuestras] = useState([]);
  const [creandoColor, setCreandoColor] = useState(false);

  // Las ocasiones propias, por la misma razón que los colores propios:
  // `/api/opciones` no tiene sesión y sirve solo las listas globales.
  const [ocasionesPropias, setOcasionesPropias] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [creandoOcasion, setCreandoOcasion] = useState(false);

  /* El historial de uso: cerrado por defecto y se pide recién al abrir.
     Pegarlo a la carga de la ficha haría pagar una consulta más a todo
     el mundo por una sección que la mayoría no abre.

     Los tres useState van ANTES del useEffect que los usa, y no es
     estilo: el arreglo de dependencias `[usosAbiertos, usos, ...]` se
     evalúa cuando se LLAMA a useEffect, durante el render. Declarado
     después, `usosAbiertos` está en zona muerta temporal y el render
     explota con ReferenceError: la ficha entera queda en blanco.
     Ni `npm run build` ni los guardianes de texto ven esto; solo
     renderizar el componente de verdad lo ve. */
  const [usosAbiertos, setUsosAbiertos] = useState(false);
  const [usos, setUsos] = useState(null);      // null = todavía no se pidió
  const [usosError, setUsosError] = useState("");

  useEffect(() => {
    if (!usosAbiertos || usos !== null) return;
    let vivo = true;
    api
      .usosDePrenda(prenda.id)
      .then((d) => { if (vivo) setUsos(d); })
      .catch((e) => { if (vivo) setUsosError(tError(e)); });
    return () => { vivo = false; };
  }, [usosAbiertos, usos, prenda.id]);

  useEffect(() => {
    api
      .listarColores()
      .then((d) => {
        setPropios(d.propios || []);
        setFamilias(d.familias || []);
        setMuestras(d.muestras || []);
      })
      .catch(() => {
        // Sin colores propios se sigue editando con los 20 de siempre.
        // No vale un cartel de error.
      });

    api
      .listarOcasiones()
      .then((d) => {
        setOcasionesPropias(d.propias || []);
        setNiveles(d.niveles || []);
      })
      .catch(() => {
        // Igual que arriba: se sigue editando con las 8 de siempre.
      });
  }, []);

  /* SE UNEN SIN DUPLICAR, y el "sin duplicar" es un arreglo
   * (2026-08-21).
   *
   * Acá había una simple concatenación, y era correcta hasta que
   * existió el sembrado de ocasiones (§14b-bis):
   *
   *   ANTES  `opciones.categorias` = las 8 base
   *          `ocasionesPropias`     = las que el usuario creó
   *          concatenar = la lista completa. Bien.
   *
   *   AHORA  para un usuario sembrado, `cargarOpciones()` ya devuelve
   *          SUS ocasiones en `opciones.categorias` (ver src/opciones.js).
   *          Concatenar las propias otra vez las cuenta dos veces.
   *
   * Se vio en pantalla: el dueño tenía 6 ocasiones y el editor le
   * mostraba 13. No es un caso raro — le pasa a todo usuario sembrado,
   * todos los días, en la pantalla que más se usa.
   *
   * Las dos fuentes siguen haciendo falta: `opciones` es el caché
   * compartido, y la consulta fresca es lo que hace que una ocasión
   * recién creada aparezca SIN recargar la página. La unión da las dos
   * cosas.
   */
  /* SE DEDUPLICA POR CLAVE NORMALIZADA, NO POR TEXTO EXACTO.
   *
   * `new Set()` compara cadenas byte a byte. Dos textos que en pantalla
   * se ven idénticos pueden no serlo:
   *
   *   "Trabajo día especial"  con la í precompuesta   (NFC)
   *   "Trabajo día especial"  con la i + tilde aparte (NFD)
   *
   * Son visualmente iguales y distintos para JavaScript. Los datos de
   * este proyecto vienen de una migración de Notion, que es exactamente
   * el escenario donde conviven las dos formas.
   *
   * Se usa la MISMA normalización que el servidor aplica para no tener
   * dos negros ni dos "Casual" (ver `normalizar` en lib/opciones.js):
   * sin acentos, sin mayúsculas, y CON los espacios. Si el servidor
   * considera que dos nombres son el mismo, la pantalla tiene que
   * considerar lo mismo — dos criterios de igualdad es cómo se fabrica
   * una lista con repetidos que nadie entiende.
   *
   * Se conserva el PRIMER nombre visto, que viene del caché de
   * opciones: es el que el resto de la app está usando.
   */
  const unir = (a, b) => {
    const vistos = new Map();
    for (const nombre of [...(a || []), ...(b || [])]) {
      if (!nombre) continue;
      const clave = claveDeNombre(nombre);
      if (!vistos.has(clave)) vistos.set(clave, nombre);
    }
    return [...vistos.values()];
  };

  const todosLosColores = unir(
    opciones.colores,
    propios.map((c) => c.nombre)
  );

  const todasLasOcasiones = unir(
    opciones.categorias,
    ocasionesPropias.map((o) => o.nombre)
  );

  const set = (campo, valor) => setF((antes) => ({ ...antes, [campo]: valor }));

  // Nunca dejar la lista vacía: la base exige al menos un valor.
  function alternar(campo, valor) {
    setF((antes) => {
      const actual = antes[campo] || [];
      const nueva = actual.includes(valor)
        ? actual.filter((v) => v !== valor)
        : [...actual, valor];
      return nueva.length ? { ...antes, [campo]: nueva } : antes;
    });
  }

  function cambios() {
    const c = {};
    for (const [campo, valor] of Object.entries(f)) {
      if (!igual(valor, prenda[campo])) c[campo] = valor;
    }
    // El nombre vacío no es un cambio, es un error de tipeo: se ignora
    // y la prenda conserva el que tenía.
    if (c.nombre !== undefined && !String(c.nombre).trim()) delete c.nombre;

    // Las estaciones se comparan contra el ORIGINAL EXPANDIDO. Una
    // prenda sin migrar tiene `estaciones` vacío y `estacion` con el
    // valor viejo: sin esto, abrir la ficha ya mostraría "1 cambio"
    // sin que nadie tocara nada.
    if (c.estaciones !== undefined) {
      const original = expandirEstaciones(prenda.estaciones ?? prenda.estacion);
      if (igual(c.estaciones, original)) delete c.estaciones;
    }

    // Ninguna estación es un estado inválido, no un cambio: la base lo
    // rechaza (cardinality >= 1) y la prenda quedaría fuera de todas
    // las combinaciones. El aviso ya está en pantalla.
    if (c.estaciones !== undefined && c.estaciones.length === 0) {
      delete c.estaciones;
    }

    return c;
  }

  async function guardar(e) {
    e.preventDefault();

    const c = cambios();
    if (Object.keys(c).length === 0) {
      onCancelar();
      return;
    }

    setGuardando(true);
    setError("");

    try {
      await onGuardar(c);
    } catch (err) {
      setError(tError(err));
      setGuardando(false);
    }
  }

  const cuantosCambios = Object.keys(cambios()).length;

  /* "PRENDA FICHADA" VIVE ACÁ, Y NO EN CADA PANTALLA.
   *
   * Estuvo dos veces en el mismo día en el lugar equivocado: primero
   * solo en la ficha (desde el armario no se podía marcar), después
   * duplicado a mano en las dos pantallas que abren este formulario.
   * Lo segundo funciona hasta que aparece una tercera pantalla y nadie
   * se acuerda — y no se acuerda nadie, porque el formulario se ve
   * completo sin el botón.
   *
   * Acá está una sola vez y viaja con el formulario a donde vaya.
   *
   * ⚠️  NO ES UN CAMPO DEL FORMULARIO, aunque esté adentro del <form>.
   *     Es `type="button"`, no toca `f` y no espera a Guardar: guarda
   *     solo, en el acto, y NO cierra la ventana. Guardar el formulario
   *     cierra; marcar que algo se mostró es un clic que se da mientras
   *     mirás la prenda, y cerrarte la pantalla por darlo sería
   *     castigarte por usarlo.
   *
   *     Si el padre no pasa `onFichada`, el botón no se dibuja. Así una
   *     pantalla que muestre este formulario sin poder guardar la marca
   *     no ofrece un botón que no hace nada. */
  async function alternarFichada() {
    if (fichando || !onFichada) return;
    setErrorFichar("");
    setFichando(true);
    try {
      await onFichada(prenda.fichada !== true);
    } catch (e) {
      setErrorFichar(tError(e));
    } finally {
      setFichando(false);
    }
  }

  return (
    <form className="editor" onSubmit={guardar}>
      {error && <div className="mensaje-error texto-mini" role="alert">{error}</div>}
      {errorFichar && <div className="mensaje-error texto-mini" role="alert">{errorFichar}</div>}

      {onFichada && (
        <button
          type="button"
          className={`chip ${prenda.fichada ? "chip-activo" : ""}`}
          onClick={alternarFichada}
          disabled={fichando}
          aria-pressed={prenda.fichada === true}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--e2)",
            marginBlockEnd: "var(--e4)",
          }}
        >
          <IconoFichada relleno={prenda.fichada === true} />
          {t("ficha.fichada", "Prenda fichada")}
        </button>
      )}

      {/* EL ORDEN DE LOS CAMPOS SIGUE UNA SOLA REGLA: de lo que más
          cambia a lo que menos (decidido con Luis el 2026-08-26).

          El motivo más común para abrir una ficha es mandar algo al
          lavadero, y `disponibilidad` estaba OCTAVA. Ahora abre la
          pantalla.

          Los tres tramos, y por qué cada campo cae donde cae:

            1. CÓMO ESTÁ HOY   disponibilidad · estado
               Lo único que cambia sin que edites la prenda.

            2. CÓMO LA USÁS    ocasiones
               Se ajusta a medida que descubrís para qué te sirve.

            3. QUÉ ES          colores · tipo+qué es · estaciones ·
                               nombre · material · talla
               Se define una vez, casi siempre corrigiendo a la IA.
               `colores` abre el tramo porque es lo que la IA más falla;
               `nombre` baja porque ya se lee en el título de la ficha y
               acá solo sirve para renombrar; `talla` va última porque
               es la que menos cambia de todas.

            4. NOTAS, al final, por pedido: es un campo de texto largo y
               arriba empujaba todo lo demás fuera de la pantalla.

          ⚠️  DOS PARES NO SE SEPARAN Y NO SE INVIERTEN. `tipo` va antes
              que `qué es` porque el tipo FILTRA el desplegable de al
              lado (ver el comentario de ese bloque); y los dos viven en
              la misma fila, así que se leen juntos. Reordenar por
              frecuencia adentro de esas filas rompería la relación que
              las hace entendibles. */}
      <div className="ficha-fila">
        <label className="campo-mini">
          <span>{t("editar.disponibilidad", "Disponibilidad")}</span>
          <select
            value={f.disponibilidad}
            onChange={(e) => set("disponibilidad", e.target.value)}
          >
            {opciones.disponibilidades.map((d) => (
              <option key={d} value={d}>{tCat("disponibilidad", d)}</option>
            ))}
          </select>
        </label>

        <label className="campo-mini">
          <span>{t("editar.estado", "Estado")}</span>
          <select value={f.estado} onChange={(e) => set("estado", e.target.value)}>
            {opciones.estados.map((e2) => (
              <option key={e2} value={e2}>{tCat("estado", e2)}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="texto-mini tenue">{t("editar.ocasiones", "Ocasiones")}</p>
      {/* Plegado: solo las elegidas (tocables: un tap la saca) más
          «Cambiar» para agregar. Ver el comentario largo de `abierto`.
          Mientras se crea una ocasión el grupo no puede plegarse: el
          formulario de crear vive adentro. */}
      <div className="opciones-chips">
        {(abierto.categorias || creandoOcasion
          ? todasLasOcasiones
          : todasLasOcasiones.filter((c) => f.categorias.includes(c))
        ).map((c) => (
          <button
            key={c}
            type="button"
            className={`chip ${f.categorias.includes(c) ? "chip-activo" : ""}`}
            onClick={() => alternar("categorias", c)}
          >
            {tCat("ocasion", c)}
          </button>
        ))}

        {/* Una ocasión que la prenda tiene marcada pero que ya no está
            en ninguna lista: se borró después de asignarla. Se muestra
            marcada y con el aviso, igual que los colores huérfanos —
            plegada también: está elegida, y esconderla haría que
            guardar la prenda la perdiera sin que nadie lo pida. */}
        {f.categorias
          .filter((c) => !todasLasOcasiones.includes(c))
          .map((c) => (
            <button
              key={c}
              type="button"
              className="chip chip-activo chip-huerfano"
              onClick={() => alternar("categorias", c)}
              title={t("editar.ocasionHuerfana", "Esta ocasión ya no existe en tus listas")}
            >
              {tCat("ocasion", c)} ⚠
            </button>
          ))}

        {!abierto.categorias && !creandoOcasion && (
          <button
            type="button"
            className="chip chip-nuevo"
            aria-expanded={false}
            onClick={() => abrir("categorias")}
          >
            {t("editar.cambiar", "Cambiar")}
          </button>
        )}

        {abierto.categorias && !creandoOcasion && (
          <button
            type="button"
            className="chip chip-nuevo"
            onClick={() => setCreandoOcasion(true)}
          >
            {t("editar.ocasionNueva", "+ Ocasión nueva")}
          </button>
        )}

        {abierto.categorias && !creandoOcasion && (
          <button
            type="button"
            className="chip chip-nuevo"
            aria-expanded={true}
            onClick={() => cerrar("categorias")}
          >
            {t("editar.listo", "Listo")}
          </button>
        )}
      </div>

      {creandoOcasion && (
        <NuevaOcasion
          niveles={niveles}
          onCreada={(ocasion) => {
            setCreandoOcasion(false);
            // Se agrega a la lista local Y se marca en la prenda, igual
            // que con los colores: el usuario la creó parado en esta
            // prenda, obligarlo a un clic más sería no haber entendido
            // para qué la creó.
            setOcasionesPropias((antes) => [...antes, ocasion]);
            alternar("categorias", ocasion.nombre);
          }}
          onCerrar={() => setCreandoOcasion(false)}
        />
      )}
      <p className="texto-mini tenue">{t("editar.colores", "Colores")}</p>
      {/* El bloque más caro del formulario: 261px abierto, 34px
          plegado (medido a 390px). Mismo patrón que Ocasiones. */}
      <div className="opciones-chips">
        {(abierto.colores || creandoColor
          ? todosLosColores
          : todosLosColores.filter((c) => f.colores.includes(c))
        ).map((c) => (
          <button
            key={c}
            type="button"
            className={`chip ${f.colores.includes(c) ? "chip-activo" : ""}`}
            onClick={() => alternar("colores", c)}
          >
            {tCat("color", c)}
          </button>
        ))}

        {/* El color de esta prenda puede no estar en ninguna lista: si
            se creó y después se borró, la prenda lo sigue teniendo. Se
            muestra igual, marcado y también plegado, para poder
            sacarlo. Esconderlo haría imposible corregir la prenda
            desde la interfaz. */}
        {f.colores
          .filter((c) => !todosLosColores.includes(c))
          .map((c) => (
            <button
              key={c}
              type="button"
              className="chip chip-activo chip-huerfano"
              onClick={() => alternar("colores", c)}
              title={t("editar.colorHuerfano", "Este color ya no existe en tus listas")}
            >
              {tCat("color", c)} ⚠
            </button>
          ))}

        {!abierto.colores && !creandoColor && (
          <button
            type="button"
            className="chip chip-nuevo"
            aria-expanded={false}
            onClick={() => abrir("colores")}
          >
            {t("editar.cambiar", "Cambiar")}
          </button>
        )}

        {abierto.colores && !creandoColor && (
          <button
            type="button"
            className="chip chip-nuevo"
            onClick={() => setCreandoColor(true)}
          >
            {t("editar.colorNuevo", "+ Color nuevo")}
          </button>
        )}

        {abierto.colores && !creandoColor && (
          <button
            type="button"
            className="chip chip-nuevo"
            aria-expanded={true}
            onClick={() => cerrar("colores")}
          >
            {t("editar.listo", "Listo")}
          </button>
        )}
      </div>

      {creandoColor && (
        <NuevoColor
          familias={familias}
          muestras={muestras}
          onCreado={(color) => {
            setCreandoColor(false);
            // Se agrega a la lista local Y se marca en la prenda. El
            // usuario creó el color estando parado en esta prenda:
            // obligarlo a hacer un clic más para asignarlo sería no
            // haber entendido para qué lo creó.
            setPropios((antes) => [...antes, color]);
            alternar("colores", color.nombre);
          }}
          onCerrar={() => setCreandoColor(false)}
        />
      )}
      {/* TIPO Y PRENDA, ENCADENADOS.

          El tipo decide qué zona del cuerpo ocupa —y con eso, si el
          armador la puede usar— así que sigue siendo el primer campo.
          La prenda concreta cuelga de él: elegido "Superior", el
          segundo desplegable ofrece solo camisas, camisetas, suéteres,
          sudaderas y ropa deportiva de torso.

          Cambiar el tipo BORRA la prenda elegida. Es a propósito:
          "Camisa de vestir" con tipo Calzado es un dato imposible, y
          dejarlo ahí esperando que el usuario se dé cuenta es peor que
          hacerle elegir de nuevo. El servidor hace lo mismo por su
          cuenta, porque la coherencia de un dato guardado no puede
          depender de que el cliente se porte bien. */}
      <div className="ficha-fila">
        <label className="campo-mini">
          <span>{t("editar.tipo", "Tipo")}</span>
          <select
            value={f.tipo}
            onChange={(e) => {
              const nuevoTipo = e.target.value;
              setF((a) => ({
                ...a,
                tipo: nuevoTipo,
                prenda: perteneceAlTipo(opciones, a.prenda, nuevoTipo)
                  ? a.prenda
                  : "",
              }));
            }}
          >
            {opciones.tipos.map((t) => (
              <option key={t} value={t}>{tCat("tipo", t)}</option>
            ))}
          </select>
        </label>

        <label className="campo-mini campo-ancho">
          <span>{t("editar.queEs", "Qué es")}</span>
          <select value={f.prenda} onChange={(e) => set("prenda", e.target.value)}>
            {/* La opción vacía queda SIEMPRE, no solo cuando está sin
                elegir: poder volver a "no sé" es parte de que el campo
                funcione. */}
            <option value="">{t("editar.sinEspecificar", "Sin especificar")}</option>
            {Object.entries(taxonomiaDe(opciones, f.tipo)).map(([sub, lista]) => (
              <optgroup key={sub} label={sub}>
                {lista.map((n) => (
                  <option key={n} value={n}>{tCat("prenda", n)}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      </div>
      {/* ESTACIONES: un conjunto, no un valor.
          Antes era un select de tres opciones y no había forma de decir
          "esto sirve en primavera y otoño pero no en verano ni
          invierno". Una campera de entretiempo tenía que ser "Todo año"
          y entonces aparecía en enero y en julio.

          Los atajos existen porque tildar dos casilleros cada vez sería
          peor que un select. Debajo quedan las cuatro sueltas para el
          caso raro. */}
      <div className="campo-estaciones">
        <span className="campo-estaciones-titulo">{t("editar.estaciones", "Estaciones")}</span>

        <div className="chips-fila">
          {Object.keys(opciones.atajosEstacion || {}).map((nombre) => (
            <button
              key={nombre}
              type="button"
              className={`chip ${atajoActivo === nombre ? "chip-activo" : ""}`}
              onClick={() => set("estaciones", expandirEstaciones(nombre))}
            >
              {tCat("preset", nombre)}
            </button>
          ))}
        </div>

        <div className="chips-fila">
          {(opciones.estaciones || []).map((e2) => (
            <button
              key={e2}
              type="button"
              className={`chip chip-chico ${
                f.estaciones.includes(e2) ? "chip-activo" : ""
              }`}
              onClick={() => alternarEstacion(e2)}
            >
              {tCat("estacion", e2)}
            </button>
          ))}
        </div>

        {f.estaciones.length === 0 && (
          <p className="mensaje-error texto-mini" style={{ margin: 0 }} role="alert">
            {t("editar.eligeEstacion", "Elige al menos una: una prenda sin estación no entra en ninguna combinación.")}
          </p>
        )}
      </div>
      <label className="campo-mini">
        <span>{t("editar.nombre", "Nombre")}</span>
        <input
          value={f.nombre}
          onChange={(e) => set("nombre", e.target.value)}
          maxLength={80}
        />
      </label>
      <div className="ficha-fila">
        <label className="campo-mini">
          <span>{t("editar.material", "Material")}</span>
          <select
            value={f.material}
            onChange={(e) => set("material", e.target.value)}
          >
            <option value="">{t("editar.sinEspecificar", "Sin especificar")}</option>
            {opciones.materiales.map((m) => (
              <option key={m} value={m}>{tCat("material", m)}</option>
            ))}
          </select>
        </label>

        <label className="campo-mini">
          {/* Texto libre a propósito: las tallas no son universales.
              Ver supabase/talla.sql. */}
          <span>{t("editar.talla", "Talla (opcional)")}</span>
          <input
            value={f.talla}
            onChange={(e) => set("talla", e.target.value)}
            placeholder={t("editar.tallaEjemplo", "M · 42 · 32x34")}
            maxLength={20}
          />
        </label>

        {/* Texto libre, como la talla: las marcas son miles y cambian
            por país. El servidor la normaliza contra las que ya tenés
            —"tommy h" termina como "Tommy Hilfiger"— así que agrupar y
            contar siguen funcionando aunque acá se escriba a las
            apuradas. Ver `normalizarMarca` en lib/rutas_prendas.js. */}
        {/* ⚠️  CON `campo-mini`, COMO SUS DOS VECINOS. Nació sin la
            clase (tanda 28, descuido): el label quedaba fuera del
            reparto flex de la fila y el texto salía al tamaño del
            cuerpo, gigante al lado de los otros dos. */}
        <label className="campo-mini">
          <span>{t("editar.marca", "Marca (opcional)")}</span>
          <input
            value={f.marca}
            onChange={(e) => set("marca", e.target.value)}
            placeholder={t("editar.marcaEjemplo", "Levis · Zara · Nike")}
            maxLength={60}
          />
        </label>
      </div>
      {/* Vacías, las notas son un chip; con contenido, el textarea de
          siempre. Una vez abiertas no se vuelven a plegar en esta
          sesión de edición: plegar un campo con el foco puesto sería
          sacarle la silla al que está escribiendo. */}
      {notasAbiertas ? (
        <label className="campo-mini">
          <span>{t("editar.notas", "Notas")}</span>
          <textarea
            value={f.notas}
            onChange={(e) => set("notas", e.target.value)}
            rows={2}
            autoFocus={!(prenda.notas || "").trim()}
          />
        </label>
      ) : (
        <div>
          <button
            type="button"
            className="chip chip-nuevo"
            aria-expanded={false}
            onClick={() => setNotasAbiertas(true)}
          >
            {t("editar.masNotas", "+ Notas")}
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          CUÁNDO USASTE ESTA PRENDA

          Va al final y cerrada porque es lo único de la ficha que NO se
          edita: el criterio del orden de los campos es "de lo que más
          cambia a lo que menos", y esto no cambia nunca desde acá.

          Es de SOLO LECTURA a propósito. Borrar un uso desde acá
          movería `veces_usada` y `ultimo_uso`, que los mantiene un
          trigger de la base y de los que depende el orden por frescura.
          Un outfit se borra en Historial, que es su pantalla.
          ═══════════════════════════════════════════════════════════ */}
      <div className="editor-usos">
        <button
          type="button"
          className="chip chip-nuevo"
          aria-expanded={usosAbiertos}
          onClick={() => setUsosAbiertos((v) => !v)}
        >
          {usosAbiertos
            ? t("editar.usosCerrar", "Ocultar cuándo la usaste")
            : t("editar.usosAbrir", "Cuándo la usaste")}
        </button>

        {usosAbiertos && (
          <div className="usos-lista">
            {usosError ? (
              <p className="mensaje-error texto-mini" role="alert">{usosError}</p>
            ) : usos === null ? (
              <p className="tenue texto-mini">{t("comun.cargando", "Cargando...")}</p>
            ) : usos.usos.length === 0 ? (
              /* Nunca usada NO es un error ni un hueco: es una prenda
                 que está esperando su turno, y el motor la pone primera
                 justamente por eso. */
              <p className="tenue texto-mini">
                {t("editar.usosNunca", "Todavía no la usaste. Va primera en las propuestas.")}
              </p>
            ) : (
              <>
                <ol className="usos-fechas">
                  {usos.usos.map((u) => (
                    <li key={u.id}>
                      <span className="usos-fecha">
                        {fechaLocal(u.fecha, { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      {u.ocasion && (
                        <span className="tenue usos-ocasion">{tCat("ocasion", u.ocasion)}</span>
                      )}
                    </li>
                  ))}
                </ol>
                {/* El total llega contado en la base. Sin él, acá diría
                    el largo de la lista —el tope— y lo llamaría "veces",
                    que es un número plausible y falso. */}
                {usos.total > usos.usos.length && (
                  <p className="tenue texto-mini">
                    {t("editar.usosTope", "Se muestran las últimas {cuantas} de {total}", {
                      cuantas: usos.usos.length,
                      total: usos.total,
                    })}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="editor-acciones">
        <button
          type="button"
          className="boton-texto texto-mini"
          onClick={onCancelar}
          disabled={guardando}
        >
          {t("editar.cancelar", "Cancelar")}
        </button>
        <button className="boton-primario boton-angosto" type="submit" disabled={guardando}>
          {guardando
            ? t("comun.guardando", "Guardando...")
            : cuantosCambios === 0
            ? t("editar.sinCambios", "Sin cambios")
            : tPlural("plural.cambios", cuantosCambios)}
        </button>
      </div>
    </form>
  );
}
